import React, { useMemo, useState } from "react";
import { Download, PenLine } from "lucide-react";
import {
  AppButton, AppDataTable, FilterToolbar, SegmentedControl, StatusBadge, UserAvatar, sortRows,
} from "../../ui";
import { formatDue, rowsInView, sortForGrading, viewCounts } from "./assignmentRules";

const DASH = <span className="tw-text-content-subtle">—</span>;

const VIEW_LABELS = {
  submitted: "To grade",
  resubmit_requested: "Sent back",
  graded: "Graded",
  not_submitted: "Not submitted",
  all: "All",
};

const workSummary = (r) => {
  const parts = [];
  if (r.fileCount) parts.push(`${r.fileCount} file${r.fileCount === 1 ? "" : "s"}`);
  if (r.hasText) parts.push("written answer");
  return parts.join(" · ");
};

/**
 * Who handed in what — and who has not — for one assignment, as a grading
 * queue. The views are the grader's jobs ("To grade" first); the default order
 * is `sortForGrading`, the same order "Save & next" walks.
 *
 * Props:
 *   rows        — `reviewRows(...)` output
 *   maxMarks
 *   view, onViewChange
 *   onOpen(submissionId)
 *   onExport, onGradeNext — toolbar actions; omitted, they are not shown
 *   loading
 */
const SubmissionsTable = ({ rows, maxMarks, view, onViewChange, onOpen, onExport, onGradeNext, loading }) => {
  const [sort, setSort] = useState(null);
  const counts = useMemo(() => viewCounts(rows), [rows]);

  const columns = useMemo(() => [
    {
      key: "student",
      label: "Student",
      sortable: true,
      sortValue: (r) => r.student?.name || "",
      render: (r) => (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          <UserAvatar name={r.student?.name || "?"} size="sm" />
          <span className="tw-flex tw-min-w-0 tw-flex-col">
            <span className="tw-truncate tw-text-[0.8125rem] tw-font-medium tw-text-content">{r.student?.name || "Unknown student"}</span>
            {r.student?.email && <span className="tw-truncate tw-text-xs tw-text-content-subtle">{r.student.email}</span>}
          </span>
        </span>
      ),
    },
    {
      key: "submittedAt",
      label: "Handed in",
      sortable: true,
      sortValue: (r) => r.submittedAt || "",
      render: (r) => (r.submittedAt ? (
        <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <span className="tw-whitespace-nowrap">{formatDue(r.submittedAt)}</span>
          {r.late && <StatusBadge status="warning">Late</StatusBadge>}
          {r.attempt > 1 && <StatusBadge status="neutral">Attempt {r.attempt}</StatusBadge>}
        </span>
      ) : DASH),
    },
    {
      key: "work",
      label: "Work",
      render: (r) => (r.submissionId ? <span className="tw-text-content-muted">{workSummary(r) || "—"}</span> : DASH),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <StatusBadge status={r.status} domain="assignmentReview" />,
    },
    {
      key: "marks",
      label: "Marks",
      align: "right",
      sortable: true,
      sortValue: (r) => (Number.isFinite(r.marks) ? r.marks : -1),
      render: (r) => (Number.isFinite(r.marks) ? (
        <span className="tw-whitespace-nowrap tw-tabular-nums">
          <span className="tw-font-semibold tw-text-content">{r.marks}</span>
          <span className="tw-text-content-subtle"> / {maxMarks}</span>
        </span>
      ) : DASH),
    },
    {
      key: "actions",
      label: "",
      width: 104,
      pinned: "right",
      resizable: false,
      // The row is clickable too, so the button must not let its click reach it.
      render: (r) => (r.submissionId ? (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <AppButton
            size="xs"
            variant={r.status === "submitted" ? "default" : "outline"}
            onClick={() => onOpen(r.submissionId)}
            aria-label={`${r.status === "submitted" ? "Grade" : "Open"} ${r.student?.name || "submission"}`}
          >
            {r.status === "submitted" ? "Grade" : "Open"}
          </AppButton>
        </div>
      ) : null),
    },
  ], [maxMarks, onOpen]);

  const visible = useMemo(() => {
    const inView = rowsInView(rows, view);
    return sort ? sortRows(inView, columns, sort) : sortForGrading(inView);
  }, [rows, view, sort, columns]);

  const emptyMessage = {
    submitted: "Nothing to grade right now.",
    resubmit_requested: "No work is waiting on a resubmission.",
    graded: "Nothing graded yet.",
    not_submitted: "Every student in the batch has handed something in.",
    all: "No students in this batch yet.",
  }[view];

  return (
    <AppDataTable
      fill={false}
      toolbar={(
        <FilterToolbar
          flush
          actions={(
            <>
              {onExport && (
                <AppButton size="sm" variant="outline" onClick={onExport} disabled={!rows.length}>
                  <Download size={16} /> Export marks
                </AppButton>
              )}
              {onGradeNext && counts.submitted > 0 && (
                <AppButton size="sm" onClick={onGradeNext}>
                  <PenLine size={16} /> Start grading
                </AppButton>
              )}
            </>
          )}
        >
          <SegmentedControl
            label="Show submissions"
            value={view}
            onChange={(next) => {
              setSort(null);
              onViewChange(next);
            }}
            options={Object.keys(VIEW_LABELS).map((key) => ({ value: key, label: VIEW_LABELS[key], count: counts[key] }))}
          />
        </FilterToolbar>
      )}
      columns={columns}
      rows={visible}
      rowKey={(r) => r.key}
      loading={loading}
      sort={sort}
      onSortChange={(key, direction) => setSort({ key, direction })}
      onRowClick={(r) => r.submissionId && onOpen(r.submissionId)}
      emptyMessage={emptyMessage}
    />
  );
};

export default SubmissionsTable;
